import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Upload a single image file to Cloudinary via backend
 * @param {File} file - The image file to upload
 * @param {string} token - Auth token
 * @param {string} folder - Folder name (default: 'comic-panels')
 * @param {function} onProgress - Progress callback (optional)
 * @returns {Promise<{success: boolean, url: string, public_id: string}>}
 */
export const uploadImage = async (file, token, folder = 'comic-panels', onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined
  });

  return response.data;
};

/**
 * Upload multiple image files to Cloudinary
 * @param {File[]} files - Array of image files
 * @param {string} token - Auth token
 * @param {string} folder - Folder name
 * @param {function} onProgress - Progress callback
 * @returns {Promise<{uploaded: Array, errors: Array}>}
 */
export const uploadMultipleImages = async (files, token, folder = 'comic-panels', onProgress = null) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  formData.append('folder', folder);

  const response = await axios.post(`${BACKEND_URL}/api/upload/images`, formData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined
  });

  return response.data;
};

/**
 * Upload a base64 encoded image to Cloudinary
 * @param {string} base64Data - Base64 encoded image (with or without data: prefix)
 * @param {string} token - Auth token
 * @param {string} folder - Folder name
 * @returns {Promise<{success: boolean, url: string, public_id: string}>}
 */
export const uploadBase64Image = async (base64Data, token, folder = 'comic-panels') => {
  const formData = new FormData();
  formData.append('image_data', base64Data);
  formData.append('folder', folder);

  const response = await axios.post(`${BACKEND_URL}/api/upload/base64`, formData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });

  return response.data;
};

/**
 * Delete an image from Cloudinary
 * @param {string} publicId - Cloudinary public ID
 * @param {string} token - Auth token
 * @returns {Promise<{success: boolean}>}
 */
export const deleteImage = async (publicId, token) => {
  const response = await axios.delete(`${BACKEND_URL}/api/upload/image/${encodeURIComponent(publicId)}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response.data;
};

/**
 * Convert a File to base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>}
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

/**
 * Check if a string is a valid Cloudinary URL
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isCloudinaryUrl = (url) => {
  return url && (
    url.includes('res.cloudinary.com') || 
    url.includes('cloudinary.com')
  );
};

/**
 * Get optimized Cloudinary URL with transformations
 * @param {string} url - Original Cloudinary URL
 * @param {object} options - Transformation options
 * @returns {string}
 */
export const getOptimizedUrl = (url, options = {}) => {
  if (!isCloudinaryUrl(url)) return url;
  
  const { width, height, quality = 'auto', format = 'auto' } = options;
  
  // Build transformation string
  let transforms = [];
  if (width) transforms.push(`w_${width}`);
  if (height) transforms.push(`h_${height}`);
  transforms.push(`q_${quality}`);
  transforms.push(`f_${format}`);
  
  const transformStr = transforms.join(',');
  
  // Insert transformation into URL
  return url.replace('/upload/', `/upload/${transformStr}/`);
};

/**
 * Get thumbnail URL for a Cloudinary image
 * @param {string} url - Original Cloudinary URL
 * @param {number} size - Thumbnail size (default: 200)
 * @returns {string}
 */
export const getThumbnailUrl = (url, size = 200) => {
  return getOptimizedUrl(url, { width: size, height: size });
};

export default {
  uploadImage,
  uploadMultipleImages,
  uploadBase64Image,
  deleteImage,
  fileToBase64,
  isCloudinaryUrl,
  getOptimizedUrl,
  getThumbnailUrl
};
